
// Purpose: Hook for multi-context session invalidation and session token validation (auth).

import { useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast as sonnerToast } from "sonner";
import { isOnProtectedRoute, validateSessionToken } from '@/utils/authUtils';

export const useSessionValidation = () => {
  // Callback for redirecting/logging out user
  const handleSessionInvalidation = () => {
    if (isOnProtectedRoute()) {
      console.log('Session invalidated, redirecting to login');
      sonnerToast.info('Sitzung beendet', {
        description: 'Sie wurden abgemeldet. Bitte melden Sie sich erneut an.'
      });
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    // Immediate session check on mount for protected routes
    const checkInitialSession = async () => {
      if (isOnProtectedRoute()) {
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          const isValid = await validateSessionToken(currentSession);
          
          if (!currentSession || !isValid) {
            console.log('No valid session on mount - redirecting to login');
            handleSessionInvalidation();
          }
        } catch (error) {
          console.log('Error checking initial session:', error);
          handleSessionInvalidation();
        }
      }
    };

    // Run initial check immediately
    checkInitialSession();

    // Detect auth changes in other browser contexts (windows, iframes, tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && (e.key.startsWith('supabase.auth.') || e.key.includes('sb-')) && !e.newValue) {
        console.log('Auth storage cleared in another tab, checking session');
        setTimeout(async () => {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (!currentSession && isOnProtectedRoute()) {
            handleSessionInvalidation();
          }
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Session polling (every 5 minutes instead of 10s to avoid unnecessary logouts)
    const sessionCheckInterval = setInterval(async () => {
      if (isOnProtectedRoute()) {
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (!currentSession) {
            console.log('No session during periodic check - redirecting');
            handleSessionInvalidation();
          }
        } catch (error) {
          // Don't logout on transient network errors
          console.log('Error during session check (ignoring):', error);
        }
      }
    }, 300000);

    // React to window focus (regain focus = check session validity)
    const handleWindowFocus = async () => {
      if (isOnProtectedRoute()) {
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          const isValid = await validateSessionToken(currentSession);

          if (!currentSession || !isValid) {
            console.log('Session invalid on window focus - redirecting');
            handleSessionInvalidation();
          }
        } catch (error) {
          console.log('Error during focus session check:', error);
          handleSessionInvalidation();
        }
      }
    };
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleWindowFocus);
      clearInterval(sessionCheckInterval);
    };
  }, []);

  return { handleSessionInvalidation };
};
